import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Button, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import employeeQualificationService from '../../../../services/EmployeeQualificationService';
import toast from 'react-hot-toast';
import { formatEmployeeQualifications } from '../../helper/helper';
import QualificationList from '../../../admin/AdminQualification/QualificationList';
import QualificationCard from './QualificationCard';

const UserQualification = ({ row, onCancelChanges }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [qboQualificationRecord, setQboQualificationRecord] = useState([]);
  const [pendingEmployeeQualifications, setPendingEmployeeQualifications] = useState([]);
  const [editClickedId, setEditClickedId] = useState(null);
  const [qualificationFile, setQualificationFile] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [qualificationChange, setQualificationChange] = useState({
    employeeKeyPayId: row?.KeyPayEmployeeId,
    pendingQualificationId: '',
    qualificationName: '',
    issueDate: new Date(),
    expiryDate: new Date(),
    referenceNumber: '',
    qualificationFile: '',
    documentId: ''
  });

  const [mandatoryQualifications, setMandatoryQualifications] = useState(row?.mandatoryQualifications || []);

  //to list the qualifications from qbo and get employee qualifications
  useEffect(() => {
    const fetchEmployeeQualificationById = async () => {
      try {
        const qboQualificationResponse = await employeeQualificationService.getQboQualification();
        const qboQualificationData = qboQualificationResponse?.data?.data;

        const keyPayEmployeeId = row?.KeyPayEmployeeId;

        const employeeQualifications = await employeeQualificationService.getEmployeeQboQualifications(
          keyPayEmployeeId
        );
        const employeeQualificationRecords = employeeQualifications?.data?.data;

        const formattedRecords = formatEmployeeQualifications(employeeQualificationRecords);

        const updateQboQualificationRecord = qboQualificationData?.map((qboRecord) => {
          const matchRecord = formattedRecords.find(
            (employeeRecord) => qboRecord.id === employeeRecord.qualificationId
          );
          if (matchRecord) {
            return { ...qboRecord, ...matchRecord };
          }
          return qboRecord;
        });
        setQboQualificationRecord(updateQboQualificationRecord);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Could not get qbo qualification');
      }
    };
    fetchEmployeeQualificationById();
  }, [row?.KeyPayEmployeeId]);

  //check if employee has modified qualifications: check in db for : employee.employeeKeyPayId, if any status is "pending"
  useEffect(() => {
    const fetchEmployeePendingQualifications = async () => {
      const response = await employeeQualificationService.getEmployeeUpdatedQualifications(row?.id);
      const pendingQualifications = response?.data?.data;

      setPendingEmployeeQualifications(pendingQualifications);
      setEmployeeId(row?.id);
    };

    fetchEmployeePendingQualifications();
  }, [row?.id]);

  const handleQualificationEdit = (qualification) => {
    setEditClickedId(qualification.qualificationId || qualification.id);
    setPreviewUrl(qualification.fileContent || '');
    setQualificationChange((prev) => ({
      ...prev,
      pendingQualificationId: qualification.qualificationId,
      qualificationName: qualification.name,
      issueDate: qualification.issueDate ? format(new Date(qualification.issueDate), 'yyyy-MM-dd') : '',
      expiryDate: qualification.expiryDate ? format(new Date(qualification.expiryDate), 'yyyy-MM-dd') : '',
      referenceNumber: qualification.cardNumber || '',
      qualificationFile: qualification.fileContent,
      documentId: qualification.documentId,
      mandatoryQualification: qualification.mandatoryQualification || false
    }));
  };

  const handleQualificationSave = () => {
    setLoading(true);

    const qualificationData = {
      employeeKeyPayId: qualificationChange?.employeeKeyPayId,
      qualificationId: qualificationChange?.pendingQualificationId,
      name: qualificationChange?.qualificationName,
      expiryDate: qualificationChange?.expiryDate,
      referenceNumber: qualificationChange?.referenceNumber,
      issueDate: qualificationChange?.issueDate,
      documentId: qualificationChange?.documentId,
      mandatory: qualificationChange?.mandatoryQualification,
      status: 'accepted',
      qualificationUrl: qualificationChange?.qualificationFile
    };

    // eslint-disable-next-line no-undef
    const formData = new FormData();
    formData.append('qualificationData', JSON.stringify(qualificationData));

    if (qualificationFile) {
      formData.append('file', qualificationFile);
    }

    // eslint-disable-next-line no-undef
    const userId = localStorage.getItem('userId');
    formData.append('userId', userId);

    employeeQualificationService
      .updateEmployeeQualificationInQboByAdmin(formData)
      .then((data) => {
        setEditClickedId(null);
        setQualificationFile('');
        setPreviewUrl('');

        const formattedRecords = formatEmployeeQualifications([data?.data?.data]);

        const updateQboQualificationRecord = qboQualificationRecord?.map((qboRecord) => {
          const matchRecord = formattedRecords.find(
            (employeeRecord) => qboRecord.id === employeeRecord.qualificationId
          );
          if (matchRecord) {
            return { ...qboRecord, ...matchRecord };
          }
          return qboRecord;
        });
        setQboQualificationRecord(updateQboQualificationRecord);

        toast.success(data?.data?.message || `Successfully updated ${qualificationData?.name}`);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || `Error while updating qualification ${qualificationData?.name}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleQualificationChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setMandatoryQualifications((prev) => {
        if (checked) {
          return [...prev, name];
        } else {
          return prev.filter((qualName) => qualName !== name);
        }
      });
    } else {
      setQualificationChange({
        ...qualificationChange,
        [name]: value
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQualificationFile(file);
      // eslint-disable-next-line no-undef
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleCancel = () => {
    setEditClickedId(null);
    setQualificationFile('');
    setPreviewUrl('');
  };

  const onMandatoryQualificationChanges = async () => {
    const mandatoryQualificationsRecord = {
      employeeId: row?.id,
      mandatoryQualifications: mandatoryQualifications
    };

    setLoading(true);

    try {
      const response = await employeeQualificationService.updateEmployeeMandatoryQualifications(
        mandatoryQualificationsRecord
      );

      toast.success(response?.data?.message || `Successfully updated mandatory qualifications`);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || `Error while updating mandatory qualifications`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ height: '800px' }}>
      <h2 style={{ borderBottom: 'none', padding: isSmall ? '10px 1rem' : '10px 2rem', overflowX: 'hidden' }}>
        {pendingEmployeeQualifications.length > 0 ? '' : 'Qualifications'}
      </h2>

      {pendingEmployeeQualifications.length > 0 ? (
        pendingEmployeeQualifications?.map((qualificationItem) => {
          return (
            <QualificationList
              qualificationItem={qualificationItem}
              key={qualificationItem[0]?.qualificationId || qualificationItem[1]?.qualificationId}
              setQualificationRecord={setPendingEmployeeQualifications}
              employeeId={employeeId}
            />
          );
        })
      ) : (
        <div style={{ padding: isSmall ? '10px 1rem' : '10px 2rem' }}>
          {qboQualificationRecord.length > 0 &&
            qboQualificationRecord.map((qualification) => {
              return (
                <QualificationCard
                  key={qualification.id}
                  qualification={qualification}
                  isEditing={editClickedId === qualification.qualificationId || editClickedId === qualification.id}
                  editData={{ ...qualificationChange, previewUrl, qualificationFile }}
                  onEdit={handleQualificationEdit}
                  onSave={handleQualificationSave}
                  onCancel={handleCancel}
                  onFieldChange={handleQualificationChange}
                  onFileChange={handleFileChange}
                  loading={loading}
                  mandatoryQualifications={mandatoryQualifications}
                />
              );
            })}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingRight: isSmall ? '1rem' : '2rem',
          paddingTop: '1rem',
          paddingBottom: '2rem'
        }}
      >
        <Button
          autoFocus
          variant="outlined"
          color="secondary"
          onClick={onCancelChanges}
          style={{ marginRight: '1rem' }}
        >
          Close
        </Button>

        <Button autoFocus variant="contained" color="primary" onClick={onMandatoryQualificationChanges}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default UserQualification;

UserQualification.propTypes = {
  row: PropTypes.any,
  onCancelChanges: PropTypes.func
};
